/**
 * Marvel IPTV Uploader Specifications
 * 
 * Single source of truth for all Marvel Stadium IPTV placements,
 * dimensions, and validation specs.
 * 
 * Placements include:
 * - IPTV Suite (grouped: Full, Side, LWrap, JWrap, Strap)
 * - General IPTV Content
 * - Stadium Square & Gate 5
 * - Function Rooms (Landscape & Portrait)
 * - Quill LED Wall
 * - Rogers LED Wall
 * - Aisle 31
 * - Gate Hubs (4, 6, 8, 9)
 * - Gate Hub Half
 */

// ============================================================
// IPTV PLACEMENTS (PILLS)
// ============================================================
export const IPTV_PLACEMENTS = [
  { label: "IPTV Suite", placementId: "IPTV-SUITE" },
  { label: "General IPTV Content", placementId: "GENERAL-AA" },
  { label: "Stadium Square & Gate 5", placementId: "STADIUM-SQUARE" },
  { label: "Function Rooms", placementId: "FR-LANDSCAPE" },
  { label: "Function Rooms (Portrait)", placementId: "FR-PORTRAIT" },
  { label: "Quill LED Wall", placementId: "FR-QUILL-LED-WALL" },
  { label: "Rogers LED Wall", placementId: "FR-ROGERS-LED-WALL" },
  { label: "Aisle 31", placementId: "A-31" },
  { label: "Gate 4", placementId: "GATE-4" },
  { label: "Gate 6", placementId: "GATE-6" },
  { label: "Gate 8 Hub", placementId: "GATE-8" },
  { label: "Gate 9 Hub", placementId: "GATE-9" },
  { label: "Gate Hub Half", placementId: "GATE-HALF" },
];

// ============================================================
// PLACEMENT → UPLOADER GROUPS
// ============================================================
export const IPTV_PLACEMENT_GROUPS = {
  "IPTV-SUITE": ["MRV-SUITE-FULL", "MRV-SUITE-SIDE", "MRV-SUITE-LWRAP", "MRV-SUITE-JWRAP", "MRV-SUITE-STRAP"],
  "GENERAL-AA": ["GENERAL-AA"],
  "STADIUM-SQUARE": ["STADIUM-SQUARE"],
  "FR-LANDSCAPE": ["FR-LANDSCAPE"],
  "FR-PORTRAIT": ["FR-PORTRAIT"],
  "FR-QUILL-LED-WALL": ["FR-QUILL-LED-WALL"],
  "FR-ROGERS-LED-WALL": ["FR-ROGERS-LED-WALL"],
  "A-31": ["A-31"],
  "GATE-4": ["GATE-4"],
  "GATE-6": ["GATE-6"],
  "GATE-8": ["GATE-8"],
  "GATE-9": ["GATE-9"],
  "GATE-HALF": ["GATE-HALF"],
};

// ============================================================
// GROUPED FOLDER PLACEMENTS
// These placements get grouped parent folders with asset name
// ============================================================
export const GROUPED_FOLDER_PLACEMENTS = new Set(["IPTV-SUITE"]);

// ============================================================
// ASSET NAME PLACEMENTS
// These placements require a user-entered Asset Name (like LED Tiled/360)
// ============================================================
export const ASSET_NAME_PLACEMENTS = new Set(["IPTV-SUITE"]);

// ============================================================
// COMMON SPECS
// ============================================================
const IPTV_VIDEO_COMMON = {
  allowed: true,
  extensions: ["mp4"],
  formats: ["AVC"],
  frameRate: 25,
  duration: 15, // Marvel IPTV has 15s duration requirement
  audio: { mode: "optional" },
};

const IPTV_IMAGE_COMMON = {
  allowed: true,
  extensions: ["png"],
};

// ============================================================
// SPEC FACTORY
// ============================================================
function singleUploaderSpec({ label, specId, prefix, width, height }) {
  return {
    label,
    specId,
    prefix,
    ledType: "iptv",
    contentGroup: "singleAsset",
    requiredFiles: 1,
    maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB per file
    dimensions: [{ width, height }],
    videoSpec: { ...IPTV_VIDEO_COMMON },
    imageSpec: { ...IPTV_IMAGE_COMMON },
  };
}

function suiteUploaderSpec({ label, specId, prefix, requiresAlpha = true }) {
  return {
    label,
    specId,
    prefix,
    ledType: "iptv",
    contentGroup: "groupedAssets",
    groupFolder: "SUITE", // Overridden at render time to include assetName: SUITE_{assetName}
    requiredFiles: 1,
    maxFiles: 1,
    maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB per file
    dimensions: [{ width: 1920, height: 1080 }],
    videoSpec: {
      ...IPTV_VIDEO_COMMON,
      duration: 15, // 15 second requirement
    },
    imageSpec: {
      ...IPTV_IMAGE_COMMON,
      requiresAlpha,
      inspectAlpha: requiresAlpha,
    },
  };
}

// ============================================================
// UPLOADER SPECS (SINGLE SOURCE OF TRUTH)
// ============================================================
export const UPLOADER_SPECS = {
  // ── IPTV Suite (5 grouped buckets) ──────────────────────────
  "MRV-SUITE-FULL": suiteUploaderSpec({
    label: "Full",
    specId: "MRV-SUITE-FULL",
    prefix: "FULL",
    requiresAlpha: false, // Full does NOT require alpha channel
  }),

  "MRV-SUITE-SIDE": suiteUploaderSpec({
    label: "Side",
    specId: "MRV-SUITE-SIDE",
    prefix: "SIDE",
  }),

  "MRV-SUITE-LWRAP": suiteUploaderSpec({
    label: "LWrap",
    specId: "MRV-SUITE-LWRAP",
    prefix: "LWRAP",
  }),

  "MRV-SUITE-JWRAP": suiteUploaderSpec({
    label: "JWrap",
    specId: "MRV-SUITE-JWRAP",
    prefix: "JWRAP",
  }),

  "MRV-SUITE-STRAP": suiteUploaderSpec({
    label: "Strap",
    specId: "MRV-SUITE-STRAP",
    prefix: "STRAP",
  }),

  // ── Single placements ──────────────────────────────────────
  "GENERAL-AA": singleUploaderSpec({
    label: "General IPTV Content",
    specId: "GENERAL-AA",
    prefix: "GAA",
    width: 1920,
    height: 1080,
  }),

  "STADIUM-SQUARE": singleUploaderSpec({
    label: "Stadium Square & Gate 5",
    specId: "STADIUM-SQUARE",
    prefix: "SSQ",
    width: 1920,
    height: 1080,
  }),

  "FR-LANDSCAPE": singleUploaderSpec({
    label: "Function Rooms",
    specId: "FR-LANDSCAPE",
    prefix: "FRLS",
    width: 1920,
    height: 1080,
  }),

  "FR-PORTRAIT": singleUploaderSpec({
    label: "Function Rooms (Portrait)",
    specId: "FR-PORTRAIT",
    prefix: "FRP",
    width: 1080,
    height: 1920,
  }),

  "FR-QUILL-LED-WALL": singleUploaderSpec({
    label: "Quill LED Wall",
    specId: "FR-QUILL-LED-WALL",
    prefix: "QLW",
    width: 1920,
    height: 768,
  }),

  "FR-ROGERS-LED-WALL": singleUploaderSpec({
    label: "Rogers LED Wall",
    specId: "FR-ROGERS-LED-WALL",
    prefix: "RLW",
    width: 1920,
    height: 768,
  }),

  "A-31": singleUploaderSpec({
    label: "Aisle 31",
    specId: "A-31",
    prefix: "A31",
    width: 1344,
    height: 576,
  }),

  "GATE-4": {
    ...singleUploaderSpec({
      label: "Gate 4",
      specId: "GATE-4",
      prefix: "G4",
      width: 1920,
      height: 540,
    }),
    videoSpec: { ...IPTV_VIDEO_COMMON, duration: null, minDuration: 15, maxDuration: 30 },
  },

  "GATE-6": {
    ...singleUploaderSpec({
      label: "Gate 6",
      specId: "GATE-6",
      prefix: "G6",
      width: 1920,
      height: 540,
    }),
    videoSpec: { ...IPTV_VIDEO_COMMON, duration: null, minDuration: 15, maxDuration: 30 },
  },

  "GATE-8": singleUploaderSpec({
    label: "Gate 8 Hub",
    specId: "GATE-8",
    prefix: "G8",
    width: 1920,
    height: 540,
  }),

  "GATE-9": singleUploaderSpec({
    label: "Gate 9 Hub",
    specId: "GATE-9",
    prefix: "G9",
    width: 1920,
    height: 540,
  }),

  "GATE-HALF": singleUploaderSpec({
    label: "Gate Hub Half",
    specId: "GATE-HALF",
    prefix: "GH",
    width: 960,
    height: 540,
  }),
};

// ============================================================
// HELPER: Build human-readable spec label
// ============================================================
export function buildSpecLabel(spec) {
  const parts = [];

  // Dimensions
  if (spec.dimensions) {
    parts.push(spec.dimensions.map((d) => `${d.width}×${d.height}`).join(" / "));
  }

  // Video
  if (spec.videoSpec) {
    parts.push("MP4");
    parts.push("H.264");
    parts.push(`${spec.videoSpec.frameRate}fps`);
    if (spec.videoSpec.minDuration && spec.videoSpec.maxDuration) {
      parts.push(`${spec.videoSpec.minDuration}–${spec.videoSpec.maxDuration}s`);
    } else if (spec.videoSpec.duration) {
      parts.push(`${spec.videoSpec.duration}s`);
    }
    parts.push("Looping");
  }

  // Image
  if (spec.imageSpec?.allowed) {
    if (Array.isArray(spec.imageSpec.extensions) && spec.imageSpec.extensions.length) {
      parts.push(
        `Static ${spec.imageSpec.extensions.map((e) => e.toUpperCase()).join(" / ")}`
      );
    } else {
      parts.push("Static Image");
    }
    parts.push(spec.imageSpec.requiresAlpha ? "RGBA" : "RGB");
  }

  return parts.join(" · ");
}

// ============================================================
// METADATA WEBHOOK (Club submissions - not commercial)
// ============================================================
export const METADATA_WEBHOOK = "https://hook.eu2.make.com/hs49hn7asl4ow6bx92kx1js28jpehkrt";

// ============================================================
// DEFAULT EXPORT
// ============================================================
const marvelIptvSpecs = {
  IPTV_PLACEMENTS,
  IPTV_PLACEMENT_GROUPS,
  GROUPED_FOLDER_PLACEMENTS,
  ASSET_NAME_PLACEMENTS,
  UPLOADER_SPECS,
  buildSpecLabel,
  METADATA_WEBHOOK,
};

export default marvelIptvSpecs;
